﻿import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import { toast } from "react-toastify";

import useListController from "@/hooks/list/useListController";
import { useHistoriesLookups, useHistories } from "@/hooks/histories/useHistories";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";
import { useIODelete } from "@/hooks/histories/useIODetail";
import { useHistoryExport } from "@/hooks/histories/useHistoryExport";

import useFilterStore from "@/stores/useFilterStore";
import { SCREEN_ID_USAGE_HISTORY } from "@/constants/screenIds";
import { buildInitialFiltersFromSearch } from "@/utils/urlFilterParams";

import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomConfirmModal from "@/components/desktop/modal/CustomConfirmModal";
import CustomProcessingModal from "@/components/desktop/modal/CustomProcessingModal";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";

import IODistribution from "@/pages/Histories/IO/IODistribution/IODistribution";
import IOAssociate from "@/pages/Histories/IO/IOAssociate/IOAssociate";
import IOForm from "@/pages/Histories/IO/IOForm/IOForm";
import IOSync from "@/pages/Histories/IO/IOSync/IOSync";

import { useUsageHistoryFilters } from "./hooks/useUsageHistoryFilters";
import { useUsageHistoryColumns } from "./hooks/useUsageHistoryColumns";

import UsageHistoryDetails from "./UsageHistoryDetails";

import { getHistoryQuickActions } from "../Home/histories-metadata";

import { USAGE_FILTERS, USAGE_METADATA, USAGE_BASE_TABLE } from "./usage-history.constants";

const ENDPOINT_TYPES = new Set(["credit_notes", "stolen"]);

const UsageHistory = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { filter } = useFilterStore();
	const { estate_parent_id, estate_id, search, parameter } = filter;
	const { canShow, canEdit, canDelete, isLoading: isLoadingPermissions } = useScreenPermissions(SCREEN_ID_USAGE_HISTORY);
	const canLoadScreenData = !isLoadingPermissions && canShow;

	const [{ initialBaseFilter, hasUrlDates }] = useState(() => {
		const { filters } = buildInitialFiltersFromSearch(USAGE_FILTERS, location.search, [
			{
				filterKey: "business_center_id",
				queryParam: "business_center_id",
				transformValue: (rawValue) => [Number.parseInt(rawValue, 10)],
			},
			{
				filterKey: "date_from",
				queryParam: "date_from",
			},
			{
				filterKey: "date_to",
				queryParam: "date_to",
			},
		]);

		return {
			initialBaseFilter: filters,
			hasUrlDates: location.search.includes("date_from=") || location.search.includes("date_to="),
		};
	});

	const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const action = searchParams.get("action");
	const historyIdParam = searchParams.get("id");
	const applicationHistoryIdParam = searchParams.get("application_history_id");
	const codeParam = searchParams.get("code");
	const integrationParam = searchParams.get("integration");
	const balanceParam = searchParams.get("balance");

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [selectedHistoryId, setSelectedHistoryId] = useState(null);

	const {
		isFilterOpen,
		filtersState,
		appliedFiltersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	} = useListController({
		baseFilter: initialBaseFilter,
		baseTable: USAGE_BASE_TABLE,
	});

	const rawMovementType = appliedFilterParams.type_movement_id ?? "";
	const currentApiType = ENDPOINT_TYPES.has(rawMovementType) ? rawMovementType : "usage";

	const isModalOpen = useMemo(() => {
		return canEdit && currentApiType === "usage" && action === "editar" && historyIdParam !== null && historyIdParam !== "";
	}, [action, canEdit, historyIdParam, currentApiType]);

	const { data: lookups, isLoading: isLoadingLookups } = useHistoriesLookups(
		{
			type: "output",
			estate_id,
			estate_parent_id,
			search,
			parameter,
		},
		{
			enabled: canLoadScreenData,
		},
	);

	const { isReady } = useUsageHistoryFilters({
		lookups,
		filtersState,
		setFilterOptions,
		handleFilterChange,
		handleApplyFilters,
		skipAutoDateInit: hasUrlDates,
	});

	const filters = useMemo(() => {
		const apiParams = { ...appliedFilterParams };
		if (ENDPOINT_TYPES.has(apiParams.type_movement_id)) {
			delete apiParams.type_movement_id;
		}
		return {
			...apiParams,
			estate_id,
			estate_parent_id,
			search,
			parameter,
			show_detail: 1,
			...tableState,
		};
	}, [appliedFilterParams, estate_id, estate_parent_id, search, parameter, tableState]);

	const { data, isLoading, isFetching } = useHistories(currentApiType, filters, {
		enabled: canLoadScreenData && !isLoadingLookups && Boolean(lookups) && isReady,
	});

	const deleteMutation = useIODelete(currentApiType, filters);

	const hasIntegration = useMemo(() => {
		const rows = data?.data || [];
		return rows.some((row) => Array.isArray(row.integration_statuses) && row.integration_statuses.length > 0);
	}, [data?.data]);

	const handleEdit = (applicationId) => {
		navigate(`/historiales/usos?action=editar&id=${applicationId}`);
	};

	const handleDelete = async (historyId) => {
		try {
			await deleteMutation.mutateAsync(historyId);
			toast.success("Aplicación eliminada correctamente.");
		} catch (error) {
			toast.error(error?.response?.data?.message || error?.response?.data?.error || "Error al eliminar la aplicación");
		}
	};

	const handleIntegrationClick = ({ code, integrationId, balanceId }) => {
		navigate(`/historiales/usos?action=sincronizacion&code=${encodeURIComponent(code)}&integration=${integrationId}&balance=${balanceId}`);
	};

	const columns = useUsageHistoryColumns(tableState, { hasIntegration, onIntegrationClick: canEdit ? handleIntegrationClick : null });

	const quickActions = useMemo(() => {
		const actions = getHistoryQuickActions("usos");
		return actions.map((action) => ({
			...action,
			onClick: () => navigate(action.path),
		}));
	}, [navigate]);

	const { handleExport, isAlreadyExporting } = useHistoryExport("usage", appliedFilterParams);

	const exportButton = useMemo(
		() => [
			{
				key: "export",
				label: "Exportar",
				icon: Download,
				variant: "secondary",
				disabled: isAlreadyExporting,
				options: [
					{ key: "excel", label: "Excel General", onClick: () => handleExport("nuevo_excel") },
					{ key: "excel_detail", label: "Excel Detalle", onClick: () => handleExport("nuevo_excel_detalle") },
					{ key: "excel_bc", label: "Por Centro de Costo", onClick: () => handleExport("nuevo_excel_bc") },
				],
			},
		],
		[handleExport, isAlreadyExporting],
	);

	const isEditableType = currentApiType === "usage";

	const isDistributionOpen = useMemo(() => {
		return canEdit && action === "distribucion" && Boolean(applicationHistoryIdParam);
	}, [canEdit, action, applicationHistoryIdParam]);

	const isSyncOpen = useMemo(() => {
		return canEdit && action === "sincronizacion" && Boolean(codeParam) && Boolean(integrationParam) && Boolean(balanceParam);
	}, [canEdit, action, codeParam, integrationParam, balanceParam]);

	const isAssociateOpen = useMemo(() => {
		return canEdit && action === "asociar_aplicacion" && Boolean(applicationHistoryIdParam);
	}, [canEdit, action, applicationHistoryIdParam]);

	const handleDistribution = (applicationHistoryId) => {
		navigate(`/historiales/usos?action=distribucion&application_history_id=${applicationHistoryId}`);
	};

	const handleAssociate = (applicationHistoryId) => {
		navigate(`/historiales/usos?action=asociar_aplicacion&application_history_id=${applicationHistoryId}`);
	};

	const renderExpandedContent = (row) => {
		return (
			<UsageHistoryDetails
				items={row.original.details || []}
				onEdit={canEdit && isEditableType ? handleEdit : null}
				onDistribution={canEdit && isEditableType ? handleDistribution : null}
				onAssociate={canEdit && isEditableType ? handleAssociate : null}
				onDelete={
					canDelete && isEditableType
						? (historyId) => {
								setSelectedHistoryId(historyId);
								setShowDeleteModal(true);
							}
						: null
				}
			/>
		);
	};

	const confirmDelete = async () => {
		if (!selectedHistoryId) return;
		await handleDelete(selectedHistoryId);
		setShowDeleteModal(false);
		setSelectedHistoryId(null);
	};

	if (isLoadingPermissions) {
		return <CustomFetching center message="Validando permisos..." />;
	}

	if (!canShow) {
		return <CustomNoPermissionView />;
	}

	return (
		<>
			<ListScaffold
				quickActions={quickActions}
				actionButtons={exportButton}
				subtitle={USAGE_METADATA.subtitle}
				filterTitle={`Filtrar ${USAGE_METADATA.title}`}
				isFilterOpen={isFilterOpen}
				filters={filtersState}
				appliedFilters={appliedFiltersState}
				onFilterChange={handleFilterChange}
				onApplyFilters={handleApplyFilters}
				onClearFilters={handleClearAllFilters}
				onClearOneFilter={handleClearOneFilter}
				onOpenFilter={openFilter}
				onCloseFilter={closeFilter}
				isFetching={isFetching}
				isLoading={isLoading || isLoadingLookups}
				data={data?.data || []}
				totalItems={data?.totalRecords || 0}
				totalPages={data?.totalPages || 0}
				toItem={data?.data?.length || 0}
				fromItem={data?.page === 1 ? 1 : (data?.page - 1) * tableState.per_page + 1}
				pageIndex={tableState.page}
				columns={columns}
				onPageChange={handlePageChange}
				pageSize={tableState.per_page}
				onPageSizeChange={handlePageSizeChange}
				sortBy={reactTableSort}
				onSortByChange={handleSortByChange}
				pageSizeOptions={[20, 50, 100, 200]}
				initialPageSize={tableState.per_page}
				initialSortBy={reactTableSort}
				compactHeaders={true}
				compactBody={true}
				useExpandableRows={true}
				renderExpandedContent={renderExpandedContent}
			/>
			<CustomConfirmModal
				open={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={confirmDelete}
				title="Eliminar aplicación"
				message={
					<div>
						¿Está seguro que desea eliminar esta aplicación?
						<br />
						<br />
						Si elimina la aplicación seleccionada, no podrá recuperarla.
						<br />
						1. Si es una aplicación de uso, se eliminará su entrada asociada.
						<br />
						2. Si es una aplicación de traspaso, se eliminará el stock cargado en el campo de destino.
					</div>
				}
				confirmText="Sí, eliminar"
				cancelText="No, cancelar"
				isLoading={deleteMutation.isLoading}
			/>
			<CustomProcessingModal open={deleteMutation.isLoading} message="Eliminando aplicación de IO..." />
			<IOForm
				isOpen={isModalOpen}
				historyId={historyIdParam}
				listType={USAGE_METADATA.apiType}
				filters={filters}
				onClose={() => navigate("/historiales/usos")}
			/>

			<IODistribution
				isOpen={isDistributionOpen}
				applicationHistoryId={applicationHistoryIdParam}
				listType={currentApiType}
				filters={filters}
				onClose={() => navigate("/historiales/usos")}
			/>

			<IOSync
				isOpen={isSyncOpen}
				code={codeParam}
				integrationId={integrationParam}
				balanceId={balanceParam}
				listType={currentApiType}
				filters={filters}
				onClose={() => navigate("/historiales/usos")}
			/>

			<IOAssociate
				isOpen={isAssociateOpen}
				applicationHistoryId={applicationHistoryIdParam}
				listType={currentApiType}
				filters={filters}
				onClose={() => navigate("/historiales/usos")}
			/>
		</>
	);
};

export default UsageHistory;
